import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Check } from 'lucide-react';
import { hu, portalGet, portalPut } from '../../lib/portalApi';

const STORE = 'oc_contest_prep_v1';

const localData = () => {
  try { return JSON.parse(localStorage.getItem(STORE) || '{}'); } catch { return {}; }
};

/** Verseny-felkészülés: heti ütemterv + víz/só napló (csak versenyző módban). */
export const ContestPrepTab = () => {
  const [showDate, setShowDate] = useState('');
  const [weight, setWeight] = useState('85');
  const [goalWeight, setGoalWeight] = useState('78');
  const [water, setWater] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  // Betöltés a fiókból; ha ott még nincs semmi, a böngészőben tárolt napló átköltözik.
  useEffect(() => {
    let alive = true;
    portalGet('/contest-prep')
      .then(async (res) => {
        if (!alive) return;
        const local = localData();
        const useLocal = !res.saved && (local.showDate || local.weight || local.water);
        const src = useLocal
          ? { show_date: local.showDate || '', weight: String(local.weight ?? '85'),
              goal_weight: String(local.goalWeight ?? '78'), water: local.water || {} }
          : res.data;
        setShowDate(src.show_date || '');
        setWeight(String(src.weight || '85'));
        setGoalWeight(String(src.goal_weight || '78'));
        setWater(src.water || {});
        if (useLocal) {
          await portalPut('/contest-prep', src).catch(() => {});
          localStorage.removeItem(STORE);
        }
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const persist = (patch) => {
    const next = { show_date: showDate, weight, goal_weight: goalWeight, water, ...patch };
    clearTimeout(timer.current);
    setSaving(true);
    timer.current = setTimeout(() => {
      portalPut('/contest-prep', next).catch(() => {}).finally(() => setSaving(false));
    }, 700);
  };

  const weeks = useMemo(() => {
    if (!showDate) return null;
    return Math.ceil((new Date(`${showDate}T12:00:00`) - new Date()) / (1000 * 3600 * 24 * 7));
  }, [showDate]);

  const weeklyLoss = useMemo(() => {
    const w = Number(weight) || 0;
    const g = Number(goalWeight) || 0;
    if (!weeks || weeks <= 0 || w <= g) return null;
    const perWeek = (w - g) / weeks;
    return {
      perWeek: Math.round(perWeek * 100) / 100,
      pct: Math.round((perWeek / w) * 1000) / 10,
      safe: perWeek / w <= 0.01,
    };
  }, [weight, goalWeight, weeks]);

  const PEAK = [
    ['7–5 nap', 'Normál víz (35–40 ml/ttkg), normál só, edzés változatlanul.'],
    ['4–3 nap', 'Szénhidrát-ürítés: alacsony CH, több zöldség, víz emelve (45–50 ml/ttkg).'],
    ['2 nap', 'Szénhidrát-feltöltés indul (6–8 g/ttkg), zsír alacsonyan, só mérsékelve.'],
    ['1 nap', 'Feltöltés folytatása, víz fokozatosan csökken, rost minimum, só visszaemelve.'],
    ['Verseny napja', 'Kis adagok, gyors CH, minimális víz kortyonként, pumpálás a színpad előtt.'],
  ];

  const WDAYS = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
        <Loader2 size={16} className="animate-spin" /> Napló betöltése…
      </div>
    );
  }

  return (
    <div data-testid="contest-prep-tab" className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-[11px] font-black uppercase tracking-[0.14em] text-gray-500 mb-4">Verseny ütemterv</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-500">Verseny dátuma</Label>
              <Input data-testid="contest-date" type="date" value={showDate}
                onChange={e => { setShowDate(e.target.value); persist({ show_date: e.target.value }); }}
                className="mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-500">Jelenlegi súly</Label>
              <Input type="number" value={weight} data-testid="contest-weight"
                onChange={e => { setWeight(e.target.value); persist({ weight: e.target.value }); }} className="mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-500">Célsúly</Label>
              <Input type="number" value={goalWeight} data-testid="contest-goal-weight"
                onChange={e => { setGoalWeight(e.target.value); persist({ goal_weight: e.target.value }); }} className="mt-1" />
            </div>
          </div>
          {weeks !== null && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-gray-900 text-white p-3">
                <div className="text-[10px] uppercase tracking-wider text-gray-400">Hátralévő idő</div>
                <div className="text-2xl font-black" data-testid="contest-weeks">{weeks > 0 ? weeks : 0}</div>
                <div className="text-[11px] text-gray-400">hét</div>
              </div>
              {weeklyLoss && (
                <div className={`rounded-lg p-3 ${weeklyLoss.safe ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">Szükséges heti fogyás</div>
                  <div className="text-2xl font-black text-gray-900">{hu(weeklyLoss.perWeek, 2)} kg</div>
                  <div className="text-[11px] text-gray-600">
                    {weeklyLoss.pct}% / hét — {weeklyLoss.safe ? 'reális tempó' : 'agresszív, izomvesztés kockázata'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-[11px] font-black uppercase tracking-[0.14em] text-gray-500 mb-4">Peak week vázlat</h3>
          <ul className="space-y-2.5">
            {PEAK.map(([d, t]) => (
              <li key={d} className="flex gap-3">
                <span className="shrink-0 text-[11px] font-black uppercase tracking-wider text-yellow-700 w-24 pt-0.5">{d}</span>
                <span className="text-sm text-gray-700 leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500 mt-4">
            Általános vázlat — a konkrét mennyiségeket mindig egyeztesd az edződdel.
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-[11px] font-black uppercase tracking-[0.14em] text-gray-500">Víz- és só napló (heti)</h3>
          <span data-testid="contest-save-state" className="text-[11px] font-bold text-gray-400 inline-flex items-center gap-1">
            {saving ? (<><Loader2 size={12} className="animate-spin" /> mentés…</>)
                    : (<><Check size={12} className="text-emerald-600" /> elmentve</>)}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="water-log-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="text-left py-2">Nap</th>
                <th className="text-left py-2">Víz (liter)</th>
                <th className="text-left py-2">Só (g)</th>
                <th className="text-left py-2">Reggeli súly (kg)</th>
              </tr>
            </thead>
            <tbody>
              {WDAYS.map(d => (
                <tr key={d} className="border-b border-gray-100">
                  <td className="py-2 font-semibold text-gray-900">{d}</td>
                  {['viz', 'so', 'suly'].map(field => (
                    <td key={field} className="py-2 pr-2">
                      <Input type="number" step="0.1" className="h-8 w-24"
                        data-testid={`water-${d}-${field}`}
                        value={water?.[d]?.[field] ?? ''}
                        onChange={e => {
                          const next = { ...water, [d]: { ...(water[d] || {}), [field]: e.target.value } };
                          setWater(next); persist({ water: next });
                        }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          A napló a fiókodban tárolódik, bármelyik eszközödön folytathatod — csak te és Gábor láthatja.
        </p>
      </div>
    </div>
  );
};

export default ContestPrepTab;
